/**
 * BackToBackCard
 * Renders back-to-back schedule data in a chat data card.
 * Shows B2B status for a specific team or all teams, plus hit rate edges by prop type.
 */

import type { BackToBackCardData } from '@parlay-picker/shared/chatTypes';
import { Calendar, TrendingDown, TrendingUp } from 'lucide-react';
import type { ReactElement } from 'react';

export function BackToBackCard({ data }: { data: BackToBackCardData }): ReactElement {
  const { team, b2bTeams, restedTeams, edges, totalSamples, oppEdges, totalOppSamples } = data;

  return (
    <div className="bg-theme-surface border border-theme-border rounded-xl p-3 space-y-3">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-theme-primary" />
        <span className="text-sm font-semibold text-theme-text">Back-to-Back Schedule</span>
      </div>

      {/* Specific team check */}
      {team && <TeamStatusSection team={team} />}

      {/* All teams check */}
      {!team && (b2bTeams || restedTeams) && (
        <AllTeamsSection b2bTeams={b2bTeams} restedTeams={restedTeams} />
      )}

      {/* Hit rate edges: teams ON B2B */}
      {edges.length > 0 && <EdgesTable edges={edges} label="ON B2B" samples={totalSamples} />}

      {/* Hit rate edges: teams FACING B2B opponents */}
      {oppEdges && oppEdges.length > 0 && (
        <EdgesTable edges={oppEdges} label="VS B2B OPPONENT" samples={totalOppSamples} />
      )}
    </div>
  );
}

// =============================================================================
// Specific Team Status
// =============================================================================

function TeamStatusSection({
  team,
}: {
  team: NonNullable<BackToBackCardData['team']>;
}): ReactElement {
  const isB2B = team.isB2B;
  const badgeClass = isB2B
    ? 'bg-theme-danger/10 text-theme-danger'
    : 'bg-theme-success/10 text-theme-success';

  return (
    <div className="flex items-center gap-2.5">
      <span className={`text-xs font-bold px-2 py-0.5 rounded ${badgeClass}`}>{team.abbr}</span>
      <div className="min-w-0">
        <div className="text-xs font-semibold text-theme-text">
          {isB2B ? 'ON A BACK-TO-BACK' : 'RESTED'}
        </div>
        {isB2B && team.lastGameDate && (
          <div className="text-[10px] text-theme-text-tertiary">
            {team.playedYesterday ? `Played yesterday (${team.lastGameDate})` : 'Double-header'}
          </div>
        )}
        {!isB2B && <div className="text-[10px] text-theme-text-tertiary">No game yesterday</div>}
      </div>
    </div>
  );
}

// =============================================================================
// All Teams Overview
// =============================================================================

function AllTeamsSection({
  b2bTeams,
  restedTeams,
}: {
  b2bTeams?: string[];
  restedTeams?: string[];
}): ReactElement {
  return (
    <div className="space-y-2">
      {b2bTeams && b2bTeams.length > 0 && (
        <div className="flex items-start gap-2">
          <TrendingDown className="w-3.5 h-3.5 text-theme-danger mt-0.5 shrink-0" />
          <div className="flex flex-wrap gap-1">
            <span className="text-[10px] text-theme-text-tertiary mr-1 self-center">B2B:</span>
            {b2bTeams.map((abbr) => (
              <span
                key={abbr}
                className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-theme-danger/10 text-theme-danger"
              >
                {abbr}
              </span>
            ))}
          </div>
        </div>
      )}
      {restedTeams && restedTeams.length > 0 && (
        <div className="flex items-start gap-2">
          <TrendingUp className="w-3.5 h-3.5 text-theme-success mt-0.5 shrink-0" />
          <div className="flex flex-wrap gap-1">
            <span className="text-[10px] text-theme-text-tertiary mr-1 self-center">Rested:</span>
            {restedTeams.map((abbr) => (
              <span
                key={abbr}
                className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-theme-success/10 text-theme-success"
              >
                {abbr}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Hit Rate Edges Table
// =============================================================================

function EdgesTable({
  edges,
  label = 'HIT RATE EDGES',
  samples,
}: {
  edges: BackToBackCardData['edges'];
  label?: string;
  samples?: number;
}): ReactElement {
  return (
    <div className="border-t border-theme-border/50 pt-2">
      <div className="text-[10px] text-theme-text-tertiary mb-1.5">{label}</div>
      {/* Table header */}
      <div className="grid grid-cols-4 gap-1 text-[9px] text-theme-text-tertiary mb-1">
        <span>Prop Type</span>
        <span className="text-center">B2B</span>
        <span className="text-center">Rested</span>
        <span className="text-right">Edge</span>
      </div>
      {/* Table rows */}
      <div className="space-y-0.5">
        {edges.map((edge) => {
          const isPositive = edge.diff > 0;
          const edgeColor = isPositive ? 'text-theme-success' : 'text-theme-danger';
          return (
            <div key={edge.label} className="grid grid-cols-4 gap-1 text-xs items-center">
              <span className="text-theme-text truncate">{edge.label}</span>
              <span className="text-theme-text-secondary text-center tabular-nums">
                {edge.b2b.toFixed(1)}%
              </span>
              <span className="text-theme-text-secondary text-center tabular-nums">
                {edge.rested.toFixed(1)}%
              </span>
              <span className={`text-right font-medium tabular-nums ${edgeColor}`}>
                {isPositive ? '+' : ''}
                {edge.diff.toFixed(1)}%
              </span>
            </div>
          );
        })}
      </div>
      {samples != null && samples > 0 && (
        <div className="text-[10px] text-theme-text-tertiary text-right mt-1">
          Based on {samples.toLocaleString()} deduped predictions
        </div>
      )}
    </div>
  );
}
